<script setup lang="ts">
// Vue & Plugins
import { computed, ref } from 'vue'

import { useShiftPublishBulkMutation } from '../../../shifts/composables/useShift'
import { useSchedulePublishDialog } from '../../composables/useSchedulePublishDialog'
import { useSchedulePublishableByArea } from '../../composables/useScheduleShiftQuery'

import { Area, AreaID } from '../../../areas/models/area'
import { Shift } from '../../../shifts/models/shift'
import { SiteID } from '../../../sites/models/site'

const { siteId } = defineProps<{
  siteId: SiteID
}>()

const { hide, isShown } = useSchedulePublishDialog()

// AppLayout mounts this dialog on every org page: load the shifts only once it's open (E2E-R4-01)
const { data: publishableByArea } = useSchedulePublishableByArea(isShown)
const publishableShiftsByArea = computed(
  () => publishableByArea.value ?? new Map<AreaID, Shift[]>(),
)

const { mutate: shiftPublishBulk, isPending: isShiftPublishBulkPending } =
  useShiftPublishBulkMutation(() => siteId)
const publishPressed = () => {
  const shiftIds = selectedShifts.value.map((shift) => shift.id)

  shiftPublishBulk(
    {
      form: { shiftIds },
    },
    {
      onSuccess: () => {
        hide()
      },
    },
  )
}

const selectedAreas = ref<Area[]>([])
const areaOptions = computed(() => {
  const options: Area[] = []
  Array.from(publishableShiftsByArea.value.values()).forEach((shiftsInArea) => {
    const area = shiftsInArea[0].area

    if (area) {
      options.push(area)
    }
  })
  return options
})

const selectedShifts = computed(() => {
  let shifts: Shift[] = []

  selectedAreas.value.forEach((area) => {
    const shiftsInArea = publishableShiftsByArea.value.get(area.id) ?? []
    shifts = shifts.concat(shiftsInArea)
  })

  return shifts
})

const getOptionLabel = (option: Area) => {
  const unpublishedShiftsCount =
    publishableShiftsByArea.value.get(option.id)?.length ?? 0
  return `${option.name} (${unpublishedShiftsCount} shifts)`
}
</script>

<template>
  <Dialog
    :visible="isShown"
    header="Publish Shifts"
    dismissable-mask
    modal
    class="w-full max-w-xl md:w-1/2"
    @update:visible="hide"
  >
    <div class="m-1 flex flex-col gap-y-4">
      <div class="flex flex-col gap-4">
        <Listbox
          v-model="selectedAreas"
          :options="areaOptions"
          :option-label="(option) => getOptionLabel(option)"
          multiple
          show-clear
          placeholder="Select areas to publish"
        />
      </div>
      <div class="flex justify-end space-x-2">
        <Button label="Cancel" text @click="hide" />
        <Button
          label="Publish"
          :loading="isShiftPublishBulkPending"
          :pt:root:class="'w-fit self-end'"
          :disabled="selectedShifts.length < 1"
          @click="publishPressed"
        />
      </div>
    </div>
  </Dialog>
</template>
